import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import GlobalThrobberComponent from '../components/global_throbber_component';
import { useNotification } from '../components/global_noification_component';
import { marked } from 'marked';
import '../css/dashboard_page.css';
import twemoji from 'twemoji';
import { publishPanel, refreshPanel } from '../util/internal_sign';

const COLLAPSE_ICON = 'https://cdn.ballhead.app/web_assets/double-left-50.png';
const EXPAND_ICON = 'https://cdn.ballhead.app/web_assets/double-right-50.png';
const CHEVRON_RIGHT = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48cG9seWxpbmUgcG9pbnRzPSI4IDQgMTQgMTAgOCAxNiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjRjY2NDM1IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==';
const CHEVRON_DOWN = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48cG9seWxpbmUgcG9pbnRzPSI0IDggMTAgMTQgMTYgOCIgc3Ryb2tlPSIjRjY2NDM1IiBmaWxsPSJub25lIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT;
const OAUTH_PAGE = process.env.REACT_APP_API_DISCORD_OAUTH_PAGE;
const PANEL_TOPIC_ID = 'TOPIC#Panel';

function DiscordSelect({ options, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const toggleOpen = () => setOpen(o => !o);
  const handleSelect = opt => {
    setSelected(opt);
    setOpen(false);
  };

  useEffect(() => {
    const handler = e => {
      if (!e.target.closest('.discord-select-wrapper')) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const parseLabel = text => ({ __html: twemoji.parse(text, { folder: 'svg', ext: '.svg' }) });

  return (
      <div
          className={`discord-select-wrapper${open ? ' open' : ''}`}
          onClick={e => {
            e.stopPropagation();
            toggleOpen();
          }}
      >
        <div className="discord-select-display">
          {selected?.emoji && <span className="option-icon">{selected.emoji}</span>}
          <span
              className="option-label"
              dangerouslySetInnerHTML={parseLabel(selected ? selected.label : placeholder)}
          />
        </div>
        {open && (
            <ul className="discord-select-options">
              {options.map((o, i) => (
                  <li
                      key={i}
                      className={`discord-select-option${selected?.value === o.value ? ' selected' : ''}`}
                      onClick={e => {
                        e.stopPropagation();
                        handleSelect(o);
                      }}
                  >
                    {o.emoji && <span className="option-icon">{o.emoji}</span>}
                    <span
                        className="option-label"
                        dangerouslySetInnerHTML={parseLabel(o.label)}
                    />
                  </li>
              ))}
            </ul>
        )}
      </div>
  );
}

function PanelControls({ notify }) {
  const [busy, setBusy] = useState(null); // null | 'publish' | 'refresh'

  const publish = async () => {
    const ch = '1383956362362617897';
    setBusy('publish');
    try {
      await publishPanel(ch);
      notify('success', 'Panel published');
    } catch (e) {
      notify('error', e.message || 'Publish failed');
    } finally {
      setBusy(null);
    }
  };

  const refresh = async () => {
    const ch = '1383956362362617897';
    setBusy('refresh');
    try {
      await refreshPanel(ch);
      notify('success', 'Panel refreshed');
    } catch (e) {
      notify('error', e.message || 'Refresh failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem' }}>
      <button
        className="primary-btn"
        onClick={publish}
        disabled={busy !== null}
      >
        {busy === 'publish' ? 'Publishing…' : 'Publish Panel'}
      </button>
      <button
        className="secondary-btn"
        onClick={refresh}
        disabled={busy !== null}
      >
        {busy === 'refresh' ? 'Refreshing…' : 'Refresh Panel'}
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const [selectedSection, setSelectedSection] = useState('Knowledge Base');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const notify = useNotification();
  const navigate = useNavigate();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [originalForm, setOriginalForm] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [newOptionEmoji, setNewOptionEmoji] = useState('');
  const [newOptionTarget, setNewOptionTarget] = useState('');
  const [creating, setCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const blockIfEditing = () => {
    if (!editForm) return false;
    if (formChanged) {
      notify('error', 'Save or cancel your edits first.');
      return true;
    }
    setEditForm(null);
    setSelectedTopicId(null);
    setOriginalForm(null);
    setCreating(false);
    return false;
  };
  const DRAFT_KEY = "kb_topic_draft";
  const TOPIC_CACHE_KEY = "kb_topic_cache";
  const [topicCache, setTopicCache] = useState(() => {
    try {
      const data = localStorage.getItem(TOPIC_CACHE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });

  const formChanged = useMemo(() => {
    if (!editForm) return false;
    if (creating) return true;
    if (!originalForm) return false;
    if (editForm.title !== originalForm.title) return true;
    if ((editForm.embedTitle || '') !== (originalForm.embedTitle || '')) return true;
    if ((editForm.embedDescription || '') !== (originalForm.embedDescription || '')) return true;
    if ((editForm.parentId || '') !== (originalForm.parentId || '')) return true;
    if ((editForm.nodeType || 'topic') !== (originalForm.nodeType || 'topic')) return true;
    if (!!editForm.hasBackButton !== !!originalForm.hasBackButton) return true;
    if ((editForm.embedColor || '') !== (originalForm.embedColor || '')) return true;
    if ((editForm.plainMessage || '') !== (originalForm.plainMessage || '')) return true;
    const optsA = editForm.dropdownOptions || [];
    const optsB = originalForm.dropdownOptions || [];
    if (optsA.length !== optsB.length) return true;
    for (let i = 0; i < optsA.length; i++) {
      const a = optsA[i] || {};
      const b = optsB[i] || {};
      if ((a.emoji || '') !== (b.emoji || '')) return true;
      if ((a.label || '') !== (b.label || '')) return true;
      if ((a.value || '') !== (b.value || '')) return true;
      if ((a.targetId || '') !== (b.targetId || '')) return true;
    }
    return false;
  }, [editForm, originalForm, creating]);

  const getTopicId = t => String(t.topic_id ?? t.id ?? t.PK);

  const flattenTopics = nodes => {
    const out = [];
    const walk = items => {
      items.forEach(n => {
        out.push({ id: getTopicId(n), title: n.title });
        if (n.children) walk(n.children);
      });
    };
    walk(nodes);
    return out;
  };

  const updateTopicCache = list => {
    const flat = flattenTopics(list);
    setTopicCache(flat);
    try {
      localStorage.setItem(TOPIC_CACHE_KEY, JSON.stringify(flat));
    } catch {}
  };

  const parseOptions = val => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const arr = JSON.parse(val);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const buildTopicTree = useCallback(list => {
    const map = {};
    list.forEach(t => {
      const id = String(getTopicId(t));
      const node = { ...t, dropdownOptions: parseOptions(t.dropdownOptions ?? t.dropdown_options), children: [] };
      map[id] = node;
      if (t.PK && String(t.PK) !== id) map[String(t.PK)] = node;
      if (t.topic_id && String(t.topic_id) !== id) map[String(t.topic_id)] = node;
      if (t.id && String(t.id) !== id) map[String(t.id)] = node;
    });
    const roots = [];
    list.forEach(t => {
      const id = String(getTopicId(t));
      const parentKey = t.parentId ?? t.parent_id ?? t.parentTopicId ?? t.parent_topic_id ?? t.parentid;
      if (parentKey != null && map[String(parentKey)]) {
        map[String(parentKey)].children.push(map[id]);
      } else {
        roots.push(map[id]);
      }
    });
    return roots;
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load topics');
      const data = await res.json();
      const structured = buildTopicTree(data.topics || []);
      setTopics(structured);
      updateTopicCache(structured);
    } catch {
      notify('error', 'Unable to fetch topics');
    }
  }, [API_BASE, API_VERSION, buildTopicTree, notify]);

  const isDuplicateValue = (value, ignoreIndex = -1) =>
      editForm?.dropdownOptions?.some((opt, idx) =>
          idx !== ignoreIndex && (opt.value || '').trim().toLowerCase() === (value || '').trim().toLowerCase()
      );

  const handleAddOption = () => {
    const label = newOptionLabel.trim();
    const val = newOptionValue.trim();
    const target = newOptionTarget.trim();
    if (!label || !val) {
      notify('error', 'Please enter both label and value for the option.');
      return;
    }
    if (editForm.dropdownOptions?.length >= 25) {
      notify('error', 'Maximum of 25 options allowed.');
      return;
    }
    if (isDuplicateValue(val)) {
      notify('error', 'Each option must have a unique value.');
      return;
    }
setEditForm({
      ...editForm,
      dropdownOptions: [...(editForm.dropdownOptions || []), { emoji: newOptionEmoji, label, value: val, targetId: target }]
    });
setNewOptionLabel('');
setNewOptionValue('');
    setNewOptionEmoji('');
setNewOptionTarget('');
  };

  const handleRemoveOption = index => {
    const updated = [...(editForm.dropdownOptions || [])];
    updated.splice(index, 1);
    setEditForm({ ...editForm, dropdownOptions: updated });
  };

  const handleOptionChange = (index, field, value) => {
    if (editingIndex !== index) setEditingIndex(index);
    const updated = [...(editForm.dropdownOptions || [])];
    updated[index] = { ...updated[index], [field]: value };
    setEditForm({ ...editForm, dropdownOptions: updated });
  };

  const updateTopicField = (id, field, value) => {
    const modify = nodes => nodes.map(n => {
      if (getTopicId(n) === id) return { ...n, [field]: value };
      if (n.children) return { ...n, children: modify(n.children) };
      return n;
    });
    setTopics(t => {
      const updated = modify(t);
      updateTopicCache(updated);
      return updated;
    });
  };

  const removeTopicFromTree = (nodes, id) => {
    let removed = null;
    const walk = items => items.reduce((acc, n) => {
      if (getTopicId(n) === id) {
        removed = n;
        return acc;
      }
      if (n.children) {
        const children = walk(n.children);
        acc.push(children !== n.children ? { ...n, children } : n);
      } else {
        acc.push(n);
      }
      return acc;
    }, []);
    return { list: walk(nodes), node: removed };
  };

  const insertTopicIntoTree = (nodes, node, parentId) => {
    if (!parentId) return [...nodes, node];
    return nodes.map(n => {
      if (getTopicId(n) === String(parentId)) {
        return { ...n, children: [...(n.children || []), node] };
      }
      if (n.children) return { ...n, children: insertTopicIntoTree(n.children, node, parentId) };
      return n;
    });
  };

  const moveTopicInTree = (nodes, id, parentId) => {
    const { list, node } = removeTopicFromTree(nodes, id);
    if (!node) return nodes;
    return insertTopicIntoTree(list, node, parentId);
  };

  const moveTopic = (id, parentId) => {
    const target = parentId || (id !== PANEL_TOPIC_ID ? PANEL_TOPIC_ID : '');
    if (String(id) === String(target)) return;
    if (target && !topicCache.some(t => String(t.id) === String(target))) return;
    setTopics(t => {
      const updated = moveTopicInTree(t, id, target);
      updateTopicCache(updated);
      return updated;
    });
    if (target) setExpandedTopics(e => ({ ...e, [target]: true }));
  };

  const updateTopicNodeInTree = (list, node) => {
    const id = getTopicId(node);
    const modify = items => items.map(n => {
      if (getTopicId(n) === id) return { ...node, children: n.children };
      if (n.children) return { ...n, children: modify(n.children) };
      return n;
    });
    return modify(list);
  };

  const updateTopicNode = node => {
    setTopics(t => {
      const updated = updateTopicNodeInTree(t, node);
      updateTopicCache(updated);
      return updated;
    });
  };

  const deleteTopic = async id => {
    if (String(id) === PANEL_TOPIC_ID) {
      notify('error', 'Main panel message cannot be deleted');
      setShowDeleteModal(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic_id: id })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to delete topic');
      }
      setTopics(t => {
        const { list } = removeTopicFromTree(t, id);
        updateTopicCache(list);
        return list;
      });
      notify('success', 'Topic deleted');
    } catch (err) {
      notify('error', err.message);
    }
    setShowDeleteModal(false);
    setEditForm(null);
    setSelectedTopicId(null);
    setOriginalForm(null);
  };

  useEffect(() => {
    const verifyUser = async () => {
      const token = Cookies.get('refresh_token');
      if (!token) {
        window.location.href = OAUTH_PAGE;
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/public/oAuth/discord/fetch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: token, application: 'dashboard' })
        });
        const data = await res.json();
        if (!res.ok) {
          notify('error', data.message || 'Authentication failed');
          navigate('/');
          return;
        }
        setUser(data.user);
        Cookies.set('discord_id', data.user.id, { sameSite: 'strict' });
        Cookies.set('username', data.user.username, { sameSite: 'strict' });
        Cookies.set('global_name', data.user.global_name, { sameSite: 'strict' });
        Cookies.set('avatar', data.user.avatar, { sameSite: 'strict' });
        Cookies.set('banner', data.user.banner, { sameSite: 'strict' });
        Cookies.set('email', data.user.email, { sameSite: 'strict' });
        Cookies.set('code', data.code, { sameSite: 'strict' });
        Cookies.set('refresh_token', data.refresh_token, { sameSite: 'strict' });
        setEligible(data.is_in_gym_guild);
        await fetchTopics();
      } catch {
        notify('error', 'Failed verifying user');
        navigate('/');
        return;
      } finally {
        setLoading(false);
      }
    };
    verifyUser()
  }, [])

  const selectTopic = topic => {
    if (blockIfEditing()) return;
    const id = getTopicId(topic);
    const nodeType = (topic.nodeType || 'topic').toLowerCase();
    const raw = topic.dropdownOptions ?? topic.dropdown_options ?? [];
    const opts = parseOptions(raw);
    const messageText = topic.plainMessage ?? topic.plain_message ?? topic.message ?? '';
    setSelectedTopicId(id);
    setEditForm({
      ...topic,
      nodeType,
      dropdownOptions: opts,
      hasBackButton: nodeType === 'topic' ? false : !!topic.hasBackButton,
      embedColor: topic.embedColor || '#F66435',
      plainMessage: messageText,
    });
    setOriginalForm({
      ...topic,
      nodeType,
      dropdownOptions: opts,
      embedColor: topic.embedColor || '#F66435',
      plainMessage: messageText,
    });
    setNewOptionLabel('');
    setNewOptionValue('');
    setNewOptionTarget('');
    setEditingIndex(-1);
  };

  const toggleTopicExpansion = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };
  const startNewTopic = () => {
    if (blockIfEditing()) return;
    const parent = selectedTopicId ? String(selectedTopicId) : PANEL_TOPIC_ID;
    const type = parent === PANEL_TOPIC_ID ? "topic" : "subtopic";
    const draftId = `draft-${Date.now()}`;
    const draft = {
      title: "",
      plainMessage: "",
      embedTitle: "",
      embedDescription: "",
      parentId: parent,
      dropdownOptions: [],
      hasBackButton: false,
      nodeType: type,
      embedColor: "#F66435",
      PK: draftId,
      children: []
    };
    const insert = nodes => {
      if (!parent) return [...nodes, draft];
      return nodes.map(n => {
        if (getTopicId(n) === parent) return { ...n, children: [...n.children, draft] };
        if (n.children) return { ...n, children: insert(n.children) };
        return n;
      });
    };
    setTopics(t => insert(t));
    if (parent) setExpandedTopics(e => ({ ...e, [parent]: true }));
    setEditForm(draft);
    setOriginalForm(null);
    setSelectedTopicId(draftId);
    setCreating(true);
    setNewOptionLabel("");
    setNewOptionValue("");
    setNewOptionEmoji("");
    setNewOptionTarget("");
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    notify("success", "Draft created");
  };

  const cancelEdit = () => {
    if (creating) {
      const id = getTopicId(editForm);
      const remove = nodes => nodes.filter(n => getTopicId(n) !== id).map(n => n.children ? { ...n, children: remove(n.children) } : n);
      setTopics(t => {
        const updated = remove(t);
        updateTopicCache(updated);
        return updated;
      });
      localStorage.removeItem(DRAFT_KEY);
      setCreating(false);
    } else if (originalForm) {
      const id = getTopicId(editForm);
      setTopics(t => {
        let updated = moveTopicInTree(t, id, originalForm.parentId || '');
        updated = updateTopicNodeInTree(updated, originalForm);
        updateTopicCache(updated);
        return updated;
      });
    }
    setEditForm(null);
    setSelectedTopicId(null);
    setOriginalForm(null);
  };

  useEffect(() => {
    if (creating && editForm) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(editForm));
    }
  }, [creating, editForm]);

  const renderTopicNode = topic => {
    const topicId = getTopicId(topic);
    const hasChildren = topic.children && topic.children.length > 0;
    const isExpanded = expandedTopics[topicId];

    return (
        <li key={topicId}>
          <div
              className={`kb-topic-header${selectedTopicId === topicId ? ' active' : ''}`}
              onMouseDown={e => e.preventDefault()}
              onClick={e => {
                e.stopPropagation();
                selectTopic(topic);
              }}
          >
            {hasChildren && (
                <button
                    className="topic-expand-btn"
                    onClick={e => {
                      e.stopPropagation();
                      toggleTopicExpansion(topicId);
                    }}
                >
                  <img
                      src={isExpanded ? CHEVRON_DOWN : CHEVRON_RIGHT}
                      alt={isExpanded ? "Collapse" : "Expand"}
                      width={16}
                      height={16}
                  />
                </button>
            )}
            <span className="kb-topic-title">{topic.title || 'Draft Topic'}</span>
          </div>
          {hasChildren && isExpanded && (
              <ul className="kb-topic-children">
                {topic.children.map(renderTopicNode)}
              </ul>
          )}
        </li>
    );
  };

  const saveEdits = async () => {
    if (!(editForm.title || '').trim()) {
      notify("error", "Title is required");
      return;
    }
    if (creating) {
      const payload = {
        title: (editForm.title || '').trim(),
        plainMessage: (editForm.plainMessage || '').trim(),
        embedTitle: (editForm.embedTitle || '').trim(),
        embedDescription: (editForm.embedDescription || '').trim(),
        parentId: editForm.parentId,
        dropdownOptions: editForm.dropdownOptions,
        hasBackButton: editForm.hasBackButton,
        nodeType: editForm.nodeType === "topic" ? "TOPIC" : "SUBTOPIC"
      };
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || "Failed to create topic");
        }
        const { id } = await res.json();
        const newTopic = { ...editForm, PK: id };
        const draftId = getTopicId(editForm);
        const replace = nodes => nodes.map(n => {
          if (getTopicId(n) === draftId) return { ...newTopic, children: n.children };
          if (n.children) return { ...n, children: replace(n.children) };
          return n;
        });
        setTopics(t => {
          const updated = replace(t);
          updateTopicCache(updated);
          return updated;
        });
        localStorage.removeItem(DRAFT_KEY);
        notify("success", "Topic created");
      } catch (err) {
        notify("error", err.message);
        return;
      }
    } else {
      const id = getTopicId(editForm);
      let sanitizedParent =
        editForm.parentId && String(editForm.parentId) !== id ? editForm.parentId : '';
      if (id !== PANEL_TOPIC_ID && !sanitizedParent) sanitizedParent = PANEL_TOPIC_ID;
      const payload = {
        id,
        nodeType: editForm.nodeType === "topic" ? "TOPIC" : "SUBTOPIC",
        title: (editForm.title || '').trim(),
        plainMessage: (editForm.plainMessage || '').trim(),
        embedTitle: (editForm.embedTitle || '').trim(),
        embedDescription: (editForm.embedDescription || '').trim(),
        dropdownOptions: editForm.dropdownOptions,
        hasBackButton: editForm.hasBackButton,
        embedColor: editForm.embedColor,
        parentId: sanitizedParent
      };
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || "Failed to update topic");
        }
      } catch (err) {
        notify("error", err.message);
        return;
      }
      setTopics(t => {
        let updated = moveTopicInTree(t, id, sanitizedParent);
        updated = updateTopicNodeInTree(updated, editForm);
        updateTopicCache(updated);
        return updated;
      });
      setExpandedTopics(e => sanitizedParent ? { ...e, [sanitizedParent]: true } : e);
      notify("success", "Topic updated");
    }
    setEditingIndex(-1);
    setSelectedTopicId(null);
    setEditForm(null);
    setOriginalForm(null);
    setCreating(false);
  };

  const renderContent = () => {
    switch (selectedSection) {
      case 'Profile':
        return (
            <div className="content-panel">
              <h2>Welcome, {user?.global_name || user?.username}</h2>
              <p>ID: {user?.id}</p>
            </div>
        );
      case 'Knowledge Base':
        return (
            <div className="content-panel">
              {editForm ? (
                  <>
                    <div className="edit-header">
                      <h2>Edit Topic</h2>
                      <div className="edit-actions">
                        <button className="secondary-btn" onClick={cancelEdit}>
                          Cancel
                        </button>
                        {formChanged && (
                            <button className="primary-btn" onClick={saveEdits}>
                              Save Changes
                            </button>
                        )}
                        <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="edit-preview-container">
                      <form className="modal-form edit-panel">
                        <div className="form-section">
                          <h3>Basic Information</h3>
                          <label>
                            Name
                            <input
                                value={editForm.title}
                                onChange={e => {
                                  const val = e.target.value;
                                  setEditForm({ ...editForm, title: val });
                                  updateTopicField(getTopicId(editForm), 'title', val);
                                }}
                                placeholder="Enter topic name"
                            />
                          </label>
                          <label>
                            Message (plain text)
                            <textarea
                              rows="2"
                              value={editForm.plainMessage || ''}
                              onChange={e => setEditForm({ ...editForm, plainMessage: e.target.value })}
                              placeholder="Enter message"
                            />
                          </label>
                          <label>
                            Embed Title
                            <input
                                value={editForm.embedTitle || ''}
                                onChange={e => setEditForm({ ...editForm, embedTitle: e.target.value })}
                                placeholder="Enter embed title"
                            />
                          </label>
                          <label>
                            Embed Description
                            <textarea
                                rows="4"
                                value={editForm.embedDescription || ''}
                                onChange={e => setEditForm({ ...editForm, embedDescription: e.target.value })}
                                placeholder="Enter embed description"
                            />
                          </label>
                          <div className="embed-settings-box">
                            <label>
                              Embed Color
                              <input
                                type="color"
                                value={editForm.embedColor || '#F66435'}
                                onChange={e => setEditForm({ ...editForm, embedColor: e.target.value })}
                              />
                            </label>
                            {editForm.nodeType !== 'topic' && (
                              <label className="embed-back-checkbox">
                                Back button
                                <input
                                  type="checkbox"
                                  checked={!!editForm.hasBackButton}
                                  onChange={e => setEditForm({ ...editForm, hasBackButton: e.target.checked })}
                                />
                              </label>
                            )}
                          </div>
                          <label>
                            Parent ID
                            <input
                                list="parent-topic-list"
                                value={editForm.parentId || ''}
                                onChange={e => {
                                  const v = e.target.value
                                  setEditForm({ ...editForm, parentId: v })
                                  const id = getTopicId(editForm)
                                  if (!v || (topicCache.some(o => String(o.id) === v) && v !== id)) {
                                    moveTopic(id, v)
                                  }
                                }}
                                placeholder="Select parent topic"
                            />
                            <datalist id="parent-topic-list">
                              {topicCache.map(o => (
                                  <option key={o.id} value={o.id}>{o.title}</option>
                              ))}
                            </datalist>
                          </label>
                          <label>
                            Type
                            <select
                                value={editForm.nodeType || 'topic'}
                                onChange={e => {
                                  const v = e.target.value.toLowerCase();
                                  setEditForm({
                                    ...editForm,
                                    nodeType: v,
                                    hasBackButton: v === 'topic' ? false : editForm.hasBackButton
                                  });
                                }}
                                className="select-field"
                            >
                              <option value="topic">Topic</option>
                              <option value="subtopic">Sub Topic</option>
                            </select>
                          </label>
                        </div>

                        <div className="form-section">
                          <h3>Dropdown Options</h3>
                          <div className="options-input">
                            <input
                                className="emoji-field"
                                placeholder="😀"
                                value={newOptionEmoji}
                                onChange={e => setNewOptionEmoji(e.target.value)}
                                maxLength={2}
                            />
                            <div className="option-text-fields">
                              <input
                                  className="option-label-field"
                                  placeholder="Option label"
                                  value={newOptionLabel}
                                  onChange={e => setNewOptionLabel(e.target.value)}
                              />
                          <input
                              className="option-value-field"
                              placeholder="Option value"
                              value={newOptionValue}
                              onChange={e => setNewOptionValue(e.target.value)}
                          />
                          <input
                              className="option-target-field"
                              list="option-topic-list"
                              placeholder="Target topic"
                              value={newOptionTarget}
                              onChange={e => setNewOptionTarget(e.target.value)}
                          />
                        </div>
                          <datalist id="option-topic-list">
                            {topicCache.map(o => (
                                <option key={o.id} value={o.id}>{o.title}</option>
                            ))}
                          </datalist>
                          </div>
                          <button
                              type="button"
                              className="add-btn add-option-btn"
                              onClick={handleAddOption}
                              disabled={editForm.dropdownOptions?.length >= 25}
                          >
                            Add Option
                          </button>
                          <div className="options-list">
                            {editForm.dropdownOptions?.map((opt, idx) => (
                                <div className="option-row" key={idx}>
                                  <input
                                      className="emoji-field"
                                      value={opt.emoji || ''}
                                      onChange={e => handleOptionChange(idx, 'emoji', e.target.value)}
                                      disabled={editingIndex !== idx}
                                      placeholder="😀"
                                      maxLength={2}
                                  />
                                  <input
                                      value={opt.label}
                                      onChange={e => handleOptionChange(idx, 'label', e.target.value)}
                                      disabled={editingIndex !== idx}
                                      placeholder="Label"
                                  />
                                  <input
                                      value={opt.value}
                                      onChange={e => handleOptionChange(idx, 'value', e.target.value)}
                                      disabled={editingIndex !== idx}
                                      placeholder="Value"
                                  />
                                  <input
                                      list="option-topic-list"
                                      value={opt.targetId || ''}
                                      onChange={e => handleOptionChange(idx, 'targetId', e.target.value)}
                                      disabled={editingIndex !== idx}
                                      placeholder="Target topic"
                                  />
                                  <div className="option-actions">
                                    <button
                                        type="button"
                                        className="edit-btn"
                                        onClick={() => {
                                          if (editingIndex === idx) {
                                            const curr = editForm.dropdownOptions[idx];
                                            if (!(curr.label || '').trim() || !(curr.value || '').trim()) {
                                              notify('error', 'Label and value cannot be empty.');
                                              return;
                                            }
                                            if (isDuplicateValue(curr.value, idx)) {
                                              notify('error', 'Each option must have a unique value.');
                                              return;
                                            }
                                            setEditingIndex(-1);
                                          } else {
                                            setEditingIndex(idx);
                                          }
                                        }}
                                    >
                                      {editingIndex === idx ? 'Save' : 'Edit'}
                                    </button>
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => handleRemoveOption(idx)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                            ))}
                          </div>
                        </div>
                      </form>
                      <div className="discord-preview">
                        <div className="preview-header">
                          <h3>Discord Preview</h3>
                          <div className="preview-tag">LIVE</div>
                        </div>
                        <div className="discord-message">
                          <img src="https://cdn.ballhead.app/web_assets/laces.png" alt="Laces" className="avatar" />
                          <div className="msg-body">
                            <div className="msg-header">
                              <span className="username">Laces</span>
                              <span className="bot-tag">BOT</span>
                              <span className="timestamp">Today&nbsp;at&nbsp;12:34&nbsp;PM</span>
                            </div>
                            {editForm.plainMessage && (
                              <div
                                className="msg-content"
                                dangerouslySetInnerHTML={{ __html: marked.parseInline(editForm.plainMessage) }}
                              />
                            )}
                            <div className="embed" style={{ borderLeftColor: editForm.embedColor || '#F66435' }}>
                              <div className="embed-accent"></div>
                              <div className="embed-body">
                                <div className="embed-title">{editForm.embedTitle || 'Embed title'}</div>
                                <div
                                    className="embed-desc"
                                    dangerouslySetInnerHTML={{ __html: marked.parse(editForm.embedDescription || 'Embed description preview') }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        {editForm.dropdownOptions?.length > 0 && (
                            <div className="components-row">
                              <DiscordSelect options={editForm.dropdownOptions} />
                            </div>
                        )}
                        {editForm.hasBackButton && (
                          <button type="button" className="back-btn-preview">
                            Back
                          </button>
                        )}
                      </div>
                    </div>
                  </>
              ) : (
                  <>
                    <h2>Knowledge Base</h2>
                    <div className="empty-state">
                      <div className="empty-icon">📚</div>
                      <p>Select or create a topic to begin editing</p>
                      <button className="primary-btn" onClick={() => startNewTopic()}>
                        Create New Topic
                      </button>
                    </div>
                  </>
              )}
            </div>
        );
      default:
        return <div className="content-panel">Select a section from the sidebar.</div>;
    }
  };

  if (loading) return <GlobalThrobberComponent />;

  if (!eligible) {
    return (
        <div className="dashboard-container">
          <div className="main-content">
            <div className="content-panel">
              <div className="unauthorized">
                <h2>Access Denied</h2>
                <p>You are not authorized to access this dashboard.</p>
              </div>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="dashboard-container">
        <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <img
                src={sidebarCollapsed ? EXPAND_ICON : COLLAPSE_ICON}
                alt={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className="collapse-icon"
            />
          </button>
          <div className="sidebar-header">
            Dashboard
          </div>
          <ul className="sidebar-nav">
            <li
                onClick={() => {
                  if (!blockIfEditing()) setSelectedSection('Profile');
                }}
                className={selectedSection === 'Profile' ? 'active' : ''}
            >
              <span className="icon">👤</span>
              <span className="label">Profile</span>
              <span className="tooltip">Profile</span>
            </li>
            <li
                onClick={() => {
                  if (!blockIfEditing()) setSelectedSection('Knowledge Base');
                }}
                className={selectedSection === 'Knowledge Base' ? 'active' : ''}
            >
              <span className="icon">📚</span>
              <span className="label">Knowledge Base</span>
              <span className="tooltip">Knowledge&nbsp;Base</span>
            </li>
          </ul>

          {!sidebarCollapsed && selectedSection === 'Knowledge Base' && (
            <>
              <div className="topics-section">
                <div className="topics-header">
                  <h3>KB Topics</h3>
                  <button
                    className="create-topic-btn sidebar-header-create-btn"
                    onClick={() => startNewTopic()}
                  >
                    + New Topic
                  </button>
                </div>
                <div className="kb-topics-wrapper">
                  <ul className="kb-topics-tree">
                    {topics.map(renderTopicNode)}
                  </ul>
                </div>
              </div>
              <div className="sidebar-toolbar">
                <button className="create-topic-btn" onClick={() => startNewTopic()}>
                  + New Topic
                </button>
                <PanelControls notify={notify} />
              </div>
            </>
          )}
        </div>

        <div className="main-content">{renderContent()}</div>
        {showDeleteModal && (
            <div className="confirm-overlay" onClick={e => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
              <div className="confirm-modal">
                <h3>Delete "{editForm?.title}"?</h3>
                <div className="actions">
                  <button className="secondary-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                  <button className="delete-btn" onClick={() => deleteTopic(getTopicId(editForm))}>Delete</button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}
